import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './Navbar.css';

function LangToggle({ className = '' }) {
  const { t, i18n } = useTranslation('common');
  const current = i18n.language?.startsWith('en') ? 'en' : 'fr';
  const toggle = () => {
    const next = current === 'fr' ? 'en' : 'fr';
    i18n.changeLanguage(next);
    localStorage.setItem('envol_lang', next);
  };
  return (
    <button onClick={toggle} className={`lang-toggle ${className}`} aria-label={t('a11y.change_lang')}>
      <span className={current === 'fr' ? 'active' : ''}>FR</span>
      <span className="lang-sep">|</span>
      <span className={current === 'en' ? 'active' : ''}>EN</span>
    </button>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled]   = useState(false);
  const [sidebarOpen, setSidebar] = useState(false);
  const [openItem, setOpenItem]   = useState(null);
  const { t } = useTranslation('common');
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setSidebar(false);
    setOpenItem(null);
  }, [location]);

  useEffect(() => {
    document.body.style.overflow = sidebarOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [sidebarOpen]);

  const toggleItem = (href) =>
    setOpenItem(prev => (prev === href ? null : href));

  // Header transparent (pas de bg blanc) tant qu'on n'a pas scrollé, sur toutes
  // les pages. Écritures/logo blancs uniquement sur l'accueil (au-dessus du hero
  // sombre) — ailleurs ils restent en couleur pour rester visibles sur fond clair.
  const isHome      = location.pathname === '/';
  const transparent = !scrolled;
  const whiteText   = transparent && isHome;

  const NAV = [
    { label: t('nav.home'), href: '/' },
    { label: t('nav.about'), href: '/a-propos', children: [
      [t('nav.about_projet'), '/a-propos#projet'],
      [t('nav.about_vision'), '/a-propos#vision'],
      [t('nav.about_objectifs'), '/a-propos#objectifs'],
      [t('nav.about_theorie'), '/a-propos#theorie'],
      [t('nav.about_zones'), '/a-propos#zones'],
      [t('nav.about_partenaires'), '/a-propos#partenaires'],
    ]},
    { label: t('nav.programs'), href: '/programmes', children: [
      [t('nav.prog_inclusion'), '/programmes#inclusion'],
      [t('nav.prog_entrepreneuriat'), '/programmes#entrepreneuriat'],
      [t('nav.prog_formation'), '/programmes#formation'],
      [t('nav.prog_agriculture'), '/programmes#agriculture'],
      [t('nav.prog_mentorat'), '/programmes#mentorat'],
      [t('nav.prog_innovation'), '/programmes#innovation'],
    ]},
    { label: t('nav.news'), href: '/actualites', children: [
      [t('nav.news_blog'), '/actualites#blog'],
      [t('nav.news_events'), '/actualites#evenements'],
      [t('nav.news_annonces'), '/actualites/annonces'],
    ]},
    { label: t('nav.gallery'), href: '/galerie' },
    { label: t('nav.cohorts'), href: '/cohortes', children: [
      [t('nav.cohorts_overview'), '/cohortes'],
      [t('nav.cohorts_dakar'), '/cohortes/dakar'],
      [t('nav.cohorts_thies'), '/cohortes/thies'],
      [t('nav.cohorts_annuaire'), '/annuaire'],
    ]},
    { label: t('nav.contact'), href: '/contact' },
  ];

  return (
    <>
      {/* ── BARRE PRINCIPALE ── */}
      <nav
        id="nav"
        className={[
          'fixed top-0 inset-x-0 z-[200] transition-all duration-[400ms]',
          'px-4 sm:px-6 md:px-10 lg:px-12 min-[1440px]:px-20 min-[1920px]:px-[120px]',
          transparent
            ? `py-[22px] bg-transparent${whiteText ? ' nav-transparent' : ''}`
            : 'py-[14px] bg-white backdrop-blur-[14px] border-b border-[rgba(37,55,70,0.10)] shadow-[0_2px_24px_rgba(37,55,70,.07)]',
        ].join(' ')}
      >
        <div className="flex items-center gap-8 max-w-[1320px] min-[1920px]:max-w-[1600px] mx-auto w-full">

          {/* Bloc 1 — Logo à gauche */}
          <div className="flex-1 flex justify-start">
            <Link to="/" className="block shrink-0">
              <img
                src={whiteText ? '/logo-horizontal-white.png' : '/logo-horizontal-color.png'}
                alt="L'envol digital pour elles"
                width="700"
                height="280"
                className={[
                  'block w-auto transition-all duration-[400ms]',
                  scrolled ? 'h-[52px] lg:h-[60px]' : 'h-[56px] lg:h-20',
                ].join(' ')}
              />
            </Link>
          </div>

          {/* Bloc 2 — Menu au centre — visible à partir de 1440px */}
          <div className="hidden lg:flex gap-4 items-center shrink-0">
            {NAV.map((item) => {
              const isActive =
                location.pathname === item.href ||
                location.pathname.startsWith(item.href + '/');
              const hasChild = item.children?.length > 0;
              return (
                <div
                  key={item.href}
                  className={`nav-item${hasChild ? ' has-child' : ''}`}
                >
                  <Link to={item.href} className={isActive ? 'active' : ''}>
                    {item.label}
                    {hasChild && <i className="caret" />}
                  </Link>
                  {hasChild && (
                    <div className="mega">
                      {item.children.map(([label, href]) => (
                        <Link key={href} to={href}>{label}</Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Bloc 3 — Langue + CTA à droite */}
          <div className="hidden lg:flex gap-4 items-center flex-1 justify-end">
            <LangToggle />
            <Link to="/candidature" className="nav-cta">
              {t('nav.apply')}
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
            </Link>
          </div>

          {/* ── Hamburger — visible sous 1440px ── */}
          <button
            className={[
              'burger',
              'lg:hidden flex flex-col justify-center items-center gap-[5px]',
              'w-10 h-10 rounded-[10px]',
              'bg-transparent border-none cursor-pointer',
              'z-[201] relative',
              'transition-colors duration-200 hover:bg-navy/[.06]',
              sidebarOpen ? 'active' : '',
            ].join(' ')}
            aria-label={sidebarOpen ? t('a11y.close_menu') : t('a11y.open_menu')}
            aria-expanded={sidebarOpen}
            onClick={() => setSidebar(v => !v)}
          >
            <span /><span /><span />
          </button>
        </div>
      </nav>

      {/* ── SIDEBAR MOBILE ── */}
      <div
        className={`sidebar-overlay${sidebarOpen ? ' open' : ''}`}
        onClick={() => setSidebar(false)}
        aria-hidden="true"
      />

      <aside className={`sidebar${sidebarOpen ? ' open' : ''}`} aria-label={t('a11y.nav_menu')}>

        <div className="sidebar-header">
          <Link to="/" className="sidebar-logo-link">
            <img src="/logo-horizontal-color.png" alt="L'envol digital pour elles" width="700" height="280" className="sidebar-logo" />
          </Link>
          <button
            className="sidebar-close"
            onClick={() => setSidebar(false)}
            aria-label={t('a11y.close_menu')}
          >
            <span /><span />
          </button>
        </div>

        <nav className="sidebar-nav">
          {NAV.map((item) => {
            const isActive =
              location.pathname === item.href ||
              location.pathname.startsWith(item.href + '/');
            const hasChild = item.children?.length > 0;
            const isOpen   = openItem === item.href;

            return (
              <div key={item.href} className={`sidebar-item${isOpen ? ' open' : ''}`}>
                {hasChild ? (
                  <button
                    className={`sidebar-link sidebar-link-toggle${isActive ? ' active' : ''}`}
                    onClick={() => toggleItem(item.href)}
                    aria-expanded={isOpen}
                  >
                    <span>{item.label}</span>
                    <i className={`sidebar-caret${isOpen ? ' open' : ''}`} />
                  </button>
                ) : (
                  <Link
                    to={item.href}
                    className={`sidebar-link${isActive ? ' active' : ''}`}
                  >
                    <span>{item.label}</span>
                  </Link>
                )}

                {hasChild && (
                  <div className={`sidebar-sub${isOpen ? ' open' : ''}`}>
                    {item.children.map(([label, href]) => (
                      <Link key={href} to={href} className="sidebar-sub-link">
                        {label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div className="sidebar-footer">
          <Link to="/candidature" className="sidebar-cta">
            {t('nav.apply')}
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
          </Link>
          <LangToggle className="lang-toggle-sidebar" />
          <p className="sidebar-tagline">L'envol digital pour elles</p>
        </div>
      </aside>
    </>
  );
}
